import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie';
import { Router } from '@angular/router';
import { sessionEndedRoute, surveyRoute } from '../route-names';
import { ElectorAndBallot } from '../ballot/backend-types'
import { SessionStorageService } from '../Services/session-storage.service';
import { AutoResume, DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { MteService } from '../Services/mte.service';
import { ApiService } from '../api/api.service';
import { environment } from '../../environments/environment';
import { KeyValuePair } from '../backend-types/misc-types';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SessionTimeoutModalComponent } from '../session-timeout-modal/session-timeout-modal.component';
import { ElectorDataService } from './elector-data.service';
import { ApiAboutService } from './api-about.service';

export interface VerifyForm {
  keyPairs: KeyValuePair[],
  pinFieldName?: string,
  locale?: string,
}

export interface Session {
  formData: VerifyForm
}

interface ErrorTypeWithTime {
  error_type: string,
  time?: string
} 

export const sessionExpiryCookieName = "__HOST-SESSION_EXPIRY";
export const csrfTokenCookieName = "XSRF-TOKEN";
export const csrfTokenHeaderName = "X-XSRF-TOKEN";

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  private readonly currentSessionSubject: BehaviorSubject<Session | null> =
    new BehaviorSubject<Session | null>(null);
  readonly currentSession: Observable<Session | null> = this.currentSessionSubject;
  sessionTimeoutDefault: number = 300000;
  secondsRemaining: number | null = 0;
  dialogRef: MatDialogRef<SessionTimeoutModalComponent> | undefined;
  endingSession: boolean = false;
  constructor(
    private readonly http: HttpClient,
    private readonly cookieService: CookieService,
    private readonly router: Router,
    private readonly mte: MteService,
    private storer: SessionStorageService,
    private idle: Idle,
    private keepalive: Keepalive,
    private apiService: ApiService,
    public announcer: LiveAnnouncer,
    private readonly dialog: MatDialog,
    private readonly userService: ElectorDataService,
    private readonly aboutService: ApiAboutService
  ) {}

  create(form: VerifyForm, recaptchaResponse: string, locale: string): Observable<ElectorAndBallot | null> {
    let requestBody: any = {
      data: form.keyPairs,
      locale: locale,
      deviceProfile: this.storer.getDeviceProfile()
    }; 
    if(!this.aboutService.isVotingCenter()) {
      requestBody["g-recaptcha-response"] = recaptchaResponse;
    }
    let response: Observable<HttpResponse<ElectorAndBallot | null>>;
    if (this.mte.useMte) {
      response = this.apiService.postWithHandshakeCheck<ElectorAndBallot | null>('/api/session/elector', requestBody);
    } else {
      response = this.http.post<ElectorAndBallot | null>(
        '/api/session/elector',
        requestBody,
        {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          observe: 'response' as 'response',
          withCredentials: true,
        },
      );
    }
    return response.pipe(
      map(r => {
        if (r.ok) {
          this.endingSession = false;
          this.currentSessionSubject.next(this.initSession(form));
          this.setupIdleTimer();
          this.idle.watch();
          return r.body;
        } else {
          this.mte.needHandshake = true;
          throw (r.body as ErrorTypeWithTime);
        }
      }),
      catchError(e => {
        this.mte.needHandshake = true;
        throw e.error;
      })
    );
  }

  authenticateFromMFA(otp: string): Observable<ElectorAndBallot | null> {
    let voterID = this.userService.userAuthData()?.voter_id ?? "";
    let apiRequest = {
      voter_id: voterID,
      otp: otp
    }
    let response: Observable<HttpResponse<ElectorAndBallot | null>>;
    if (this.mte.useMte) {
      response = this.apiService.postWithHandshakeCheck<ElectorAndBallot | null>('/api/session/verifyotp', apiRequest);
    } else {
      response = this.http.post<ElectorAndBallot | null> (
        '/api/session/verifyotp',
        apiRequest,
        {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          observe: 'response' as 'response',
          withCredentials: true,
        },
      );
    }
    return response.pipe(
      map(r => {
        if (r.ok) {
          this.currentSessionSubject.next(this.initSession({keyPairs: [{key: "voterID", value: voterID}]}));
          this.endingSession = false;
          this.setupIdleTimer();
          this.idle.watch();
          return r.body;
        } else {
          this.mte.needHandshake = true;
          throw (r.body as ErrorTypeWithTime);
        }
      }),
      catchError(e => {
        this.mte.needHandshake = true;
        throw e.error;
      })
    );
  }  

  finishedVoting() {
    if (environment.surveyLink.length > 0) {
      this.killSession()
      this.router.navigate(surveyRoute)
    } else {
      this.endSession()
    }
  }

  endSession(): void {
    if(this.aboutService.vwaFlowType === 'GLOBAL'){
      this.aboutService.headerImageUrl.set(undefined);
      this.aboutService.headerTitle.set("");
      this.aboutService.headerDates.set("");
    }
    this.apiService.post('/api/endsession', {}).subscribe({
      next: () => {
        this.killSession();
      },
      error: () => {
        this.closeAllDialogs();
      },
      complete: () => {
      }
    })
    this.router.navigate(sessionEndedRoute);
  }

  killSession() {
    this.mte.needHandshake = true;
    this.mte.clientDecoder?.uninstantiate;
    this.mte.clientDecoder?.destruct;
    this.mte.clientEncoder?.uninstantiate;
    this.mte.clientEncoder?.destruct;
    this.storer.sessionDecoder?.uninstantiate;
    this.storer.sessionDecoder?.destruct;
    this.storer.sessionEncoder?.uninstantiate;
    this.storer.sessionEncoder?.destruct;
    this.storer.removeAll();
    this.cookieService.removeAll();
    this.currentSessionSubject.next(null);
    this.closeAllDialogs();
  }

  refreshSession(): Observable<object> {
    this.resetIdleTimer();
    return this.http.put(
      '/api/session',
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), observe: 'response' as 'response', withCredentials: true }
    );
  }

  setupIdleTimer() {
    const millisecondsLeft = new Date(this.cookieService.get(sessionExpiryCookieName) ?? Date.now() + this.sessionTimeoutDefault).getTime() - Date.now();
    const secondsLeft = Math.round(millisecondsLeft / 1000);
    const idleInterval = secondsLeft - 30;
    this.idle.stop();
    this.idle.setIdle(idleInterval);
    this.idle.setTimeout(30);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.onIdleStart.subscribe(() => {
      if (!this.dialogRef) {
        this.dialogRef = this.dialog.open(SessionTimeoutModalComponent);
      }
      this.announcer.announce($localize` Your session will end in 30 seconds due to inactivity, click “Continue” to extend the session.`, "assertive");
    })
    this.idle.onTimeout.subscribe(() => {
      if(!this.endingSession) {
        this.endingSession = true;
        this.endSession();
      }
      this.closeAllDialogs();

      this.announcer.announce($localize` Your session has been closed`, "assertive");
    })

    this.idle.onTimeoutWarning.subscribe((seconds) => {
      this.secondsRemaining = seconds;
    })
    this.idle.setAutoResume(AutoResume.notIdle);
    this.idle.setKeepaliveEnabled(true);
    this.keepalive.interval(idleInterval);
    this.keepalive.request(new HttpRequest("PUT", '/api/session', { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), observe: 'response' as 'response', withCredentials: true }));
  }

  closeAllDialogs() {
    this.dialog.closeAll();
    this.dialogRef?.afterClosed().subscribe(() => {
      this.dialogRef = undefined;
    });
  }

  resetIdleTimer() {
    this.idle.watch();
    this.secondsRemaining = null;
    this.closeAllDialogs();
  }

  private initSession(form: VerifyForm): Session {
    return { formData: form }
  }

  activeSessionHere(): boolean {
    return this.idle.isRunning();
  }

  // Returns true/false if there is an active session in this or another tab that is going to stay open.
  activeSessionCheck(): boolean {
    let cookieDict = this.cookieService.getAll();
    if(cookieDict[sessionExpiryCookieName] && !this.endingSession) {
      return true;
    } else {
      return false;
    }
  }

  partiallyDeletedCookies(): boolean {
    let cookieDict = this.cookieService.getAll();
    if(cookieDict[sessionExpiryCookieName] && cookieDict[csrfTokenCookieName] === undefined) {
      return true;
    } else {
      return false;
    }
  }

  get xsrfCookieName(): string | undefined {
    return this.cookieService.get(csrfTokenCookieName);
  }
}